import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { getDaysBetween } from '@app/core/utils/get-days-between';

/**
 * Custom Angular validator factory that checks if the date range between two form controls
 * does not exceed a specified number of calendar months.
 *
 * Uses proper month arithmetic (e.g. Jan 1 + 3 months = Apr 1) rather than a fixed day count.
 *
 * @param startControlName - The name of the form control containing the start date.
 * @param endControlName - The name of the form control containing the end date.
 * @param maxMonths - The maximum number of calendar months allowed between start and end.
 * @returns A validator function that returns `{ dateRangeMonthsExceeded: { maxMonths } }` when
 *          the end date is more than maxMonths after the start date, or `null` when valid.
 *
 * @example
 * ```typescript
 * const form = new FormGroup({
 *   start: new FormControl(null, [Validators.required]),
 *   end:   new FormControl(null, [Validators.required]),
 * });
 *
 * form.get('end')?.addValidators(dateRangeMonthsValidator('start', 'end', 3));
 *
 * // Revalidate end whenever start changes
 * form.get('start')?.valueChanges.subscribe(() => {
 *   form.get('end')?.updateValueAndValidity({ emitEvent: false });
 * });
 * ```
 */
export function dateRangeMonthsValidator(
  startControlName: string,
  endControlName: string,
  maxMonths: number
) {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent as FormGroup;

    if (!formGroup) return null;

    const start = formGroup.get(startControlName)?.value;
    const end = formGroup.get(endControlName)?.value;

    if (!start || !end) return null;

    const startDate = new Date(start);
    const endDate = new Date(end);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;

    const limit = new Date(startDate);
    limit.setMonth(limit.getMonth() + maxMonths);

    if (endDate > limit) {
      return { dateRangeMonthsExceeded: { maxMonths } };
    }

    return null;
  };
}

/**
 * Custom Angular validator factory that checks if the date range between two form controls
 * is within specified minimum and maximum number of days.
 *
 * Can be used in reactive forms to ensure the user selects a valid date range.
 *
 * @param startControlName - The name of the form control containing the start date.
 * @param endControlName - The name of the form control containing the end date.
 * @param minDays - The minimum number of days required between start and end dates.
 * @param maxDays - The maximum number of days allowed between start and end dates.
 * @returns A validator function that returns a validation error object:
 *          - `{ dateRangeExceeded: { maxDays } }` if the range exceeds the maximum limit
 *          - `{ dateRangeTooShort: { minDays } }` if the range is below the minimum limit
 *          - `null` if the range is valid or dates are not set.
 *
 * @example
 * ```typescript
 * const form = new FormGroup({
 *   start: new FormControl(null, [Validators.required]),
 *   end: new FormControl(null, [Validators.required])
 * });
 *
 * form.get('start')?.addValidators(dateRangeValidator('start', 'end', 1, 10));
 * form.get('end')?.addValidators(dateRangeValidator('start', 'end', 1, 10));
 *
 * // Cross-validation: revalidate the other field when one changes
 * form.get('start')?.valueChanges.subscribe(() => {
 *   form.get('end')?.updateValueAndValidity({ emitEvent: false });
 * });
 * form.get('end')?.valueChanges.subscribe(() => {
 *   form.get('start')?.updateValueAndValidity({ emitEvent: false });
 * });
 * ```
 */
export function dateRangeValidator(
  startControlName: string,
  endControlName: string,
  minDays: number,
  maxDays: number
) {
  return (control: AbstractControl): ValidationErrors | null => {
    const formGroup = control.parent as FormGroup;

    if (!formGroup) {
      return null;
    }

    const startControl = formGroup.get(startControlName);
    const endControl = formGroup.get(endControlName);

    if (!startControl || !endControl) {
      return null;
    }

    const start = startControl.value;
    const end = endControl.value;

    if (!start || !end) {
      return null;
    }

    const dayCount = getDaysBetween(start, end);
    if (dayCount > maxDays) {
      return { dateRangeExceeded: { maxDays } };
    }
    if (dayCount < minDays) {
      return { dateRangeTooShort: { minDays } };
    }

    return null;
  };
}
